import type { SkipReason, SkipEntry, DreamStats } from "./types";
import { SOURCES } from "./types";

const MAX_SAMPLE_IDS = 5;

// The post-fetch facts classifyGroup needs to assign exactly one terminal state.
// I/O (items fetch + trace recovery) happens in the pipeline; the bucketing
// RULES live here so they are pure and unit-testable.
export interface ClassifyInput {
  group: {
    conversationId?: string;
    source: string;
    worker: string;
    responseId?: string;
    delegationId?: string;
  };
  isTargetWorker: boolean;   // worker (alias-resolved) === requested
  hasConvId: boolean;        // conversationId present on the group
  recovered: boolean;        // a trace yielded a conv_ id for a conv-less group
  itemCount: number;         // usable items returned by the transcript fetch
  fetchError?: "not-found" | "terminal" | "auth"; // a transcript/recovery read failed
  knownSource: boolean;      // group.source ∈ SOURCES
  inTraceWindow: boolean;    // the trace has NOT aged out (only meaningful when !hasConvId)
}

export type GroupOutcome = "consumed" | "recovered" | SkipReason;

/**
 * ordered, exhaustive, mutually-exclusive partition. Returns exactly one
 * outcome per group, in the precedence order. Pure — no I/O, no clock.
 *
 * Precedence (matches the evaluation order below): no-attribution →
 *   not-target-worker → unknown-source →
 *   (consumed | conv-fetch-failed)  // conv-id path
 *   (recovered | recovery-no-content)  // recovered conv-id path
 *   outside-trace-window  // aged-out window evaluated BEFORE the a2a class
 *   a2a-unlinked  // a2a evaluated BEFORE no-conv-id (avoids double-count)
 *   no-conv-id
 */
export function classifyGroup(input: ClassifyInput): GroupOutcome {
  const { group, isTargetWorker, hasConvId, recovered, itemCount, fetchError, knownSource, inTraceWindow } = input;

  // 1. no-attribution — the no-metadata class (no worker on the group at all).
  // Evaluated before not-target-worker: a worker-less group can't be matched to a
  // target, so it can never be the requested worker — it is unattributed, full stop.
  if (!group.worker) return "no-attribution";

  // 2. not-target-worker (incl. smoke/test long tail) — alias-resolved upstream.
  if (!isTargetWorker) return "not-target-worker";

  // 8. unknown-source — defensive, but it outranks any consume/recover so a
  // stray source value is never normalized or silently dropped.
  if (!knownSource) return "unknown-source";

  // conv-id path: we attempted a transcript fetch.
  if (hasConvId) {
    if (fetchError === "auth") return "auth-failed";  // Azure/Foundry cred failure, NOT a missing conv
    if (fetchError) return "conv-fetch-failed";       // 6. 404 / 500-exhausted
    if (itemCount >= 1) return "consumed";            // — content present
    return "conv-fetch-failed";                       // empty fetch via a conv-id is a failed read
  }

  // recovered conv-id path: a trace yielded a conv_ id and we fetched its items.
  if (recovered) {
    if (fetchError === "auth") return "auth-failed";  // cred failure on the recovered fetch
    if (fetchError) return "conv-fetch-failed";       // 6. recovered id 404'd on fetch
    if (itemCount >= 1) return "recovered";           // — content-proven
    return "recovery-no-content";                     // 5. recovered id, 0 usable items
  }

  // No conv-id and no recovery.
  // 7. conv-less + responseId but the trace aged out: when there WAS a responseId
  // to recover from and the trace window has closed, the aged-out window is the
  // terminal reason — evaluated BEFORE the a2a class so a recoverable-but-expired
  // a2a group is bucketed as outside-trace-window rather than a2a-unlinked.
  if (group.responseId && !inTraceWindow) return "outside-trace-window";

  // 3. a2a is its own class, evaluated BEFORE no-conv-id (avoids double-count).
  const isA2a = group.source === "a2a";
  if (isA2a) return "a2a-unlinked";

  // 4. non-a2a, has worker, no conv-id, no responseId left to recover.
  return "no-conv-id";
}

/** Accumulates skips into SkipEntry shape: {reason, count, sampleIds, lastError}. */
export class SkipLedger {
  private byReason = new Map<SkipReason, SkipEntry>();

  addSkip(reason: SkipReason, id: string, err?: string): void {
    let entry = this.byReason.get(reason);
    if (!entry) {
      entry = { reason, count: 0, sampleIds: [] };
      this.byReason.set(reason, entry);
    }
    entry.count += 1;
    if (entry.sampleIds.length < MAX_SAMPLE_IDS) entry.sampleIds.push(id);
    if (err !== undefined) entry.lastError = err;
  }

  /** Σ of all skip counts. */
  total(): number {
    let sum = 0;
    for (const e of this.byReason.values()) sum += e.count;
    return sum;
  }

  /** The skip-ledger array for the NormalizedDreamInput contract. */
  entries(): SkipEntry[] {
    return [...this.byReason.values()];
  }
}

/**
 * The structural guarantee that nothing is silently dropped:
 * consumed + recovered + Σ(skips) == groups. Throws loudly otherwise.
 */
export function assertInvariant(stats: DreamStats, groups: number): void {
  const sum = stats.consumed + stats.recovered + stats.skipped;
  if (sum !== groups) {
    throw new Error(
      `partition invariant violated: consumed(${String(stats.consumed)}) + recovered(${String(stats.recovered)}) + skipped(${String(stats.skipped)}) = ${String(sum)} !== groups(${String(groups)})`,
    );
  }
}

export { SOURCES };
